import { Component, Inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';

import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskOrderStatusBlockConfig } from '@front-platform/content-builder';
import { kioskOrderStatusBlockResolveConfig } from '@front-platform/content-builder';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { DoodOrderModel } from '@store/order/order.model';
import { TextAtomComponent } from '@shared/atoms/text-atom/text-atom.component';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { FitTextDirective } from '@shared/directives/fit-text.directive';
import { ChangeLangHandlerService } from '@core/services/i18n/change-lang-handler.service';
import { ResetStateService } from '@core/services/reset-state.service';
import {
  KIOSK_ORDER_STATUS_BLOCK_DATA_SOURCE,
  KioskOrderStatusBlockDataSource,
} from './kiosk-order-status-block-view-model';
import { provideKioskOrderStatusBlockDataSource } from './kiosk-order-status-block-data-source.providers';

export interface KioskOrderStatusBlockRuntimeConfig {
  'footer1'?: string | undefined;
  'footer2'?: string | undefined;
  'display_order_ready_at'?: boolean;
}

export type KioskOrderStatusBlockConfigInput = Partial<
  Omit<KioskOrderStatusBlockConfig, keyof KioskOrderStatusBlockRuntimeConfig> &
    KioskOrderStatusBlockRuntimeConfig
>;
export type KioskOrderStatusBlockResolvedConfig = Omit<
  KioskOrderStatusBlockConfig,
  keyof KioskOrderStatusBlockRuntimeConfig
> &
  Partial<KioskOrderStatusBlockRuntimeConfig>;
export interface KioskOrderStatusBlockContract extends ConfigComponentContract<
  KioskOrderStatusBlockConfigInput,
  KioskOrderStatusBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-order-status-block',
  templateUrl: './kiosk-order-status-block.component.html',
  styleUrls: ['./kiosk-order-status-block.component.scss'],
  providers: [ChangeLangHandlerService, ...provideKioskOrderStatusBlockDataSource()],
  imports: [
    CommonModule,
    TranslateModule,
    ButtonAtomComponent,
    TextAtomComponent,
    FitTextDirective,
  ],
})
export class KioskOrderStatusBlockComponent
  extends DestroyerBase
  implements KioskOrderStatusBlockContract
{
  readonly config = input<KioskOrderStatusBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskOrderStatusBlockConfig,
    KioskOrderStatusBlockRuntimeConfig
  >(kioskOrderStatusBlockResolveConfig, () => ({ config: this.config() }));

  get headerImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['header_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }

  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }

  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }

  get buttonBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor;
  }

  get buttonTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor;
  }

  get logo(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['logo'] as unknown as IContentBuilderFieldImage | undefined;
  }

  get heading(): string {
    return this.resolvedConfig()['heading'] as unknown as string;
  }

  get footer(): string {
    return this.resolvedConfig()['footer'] as unknown as string;
  }

  get footer1(): string | undefined {
    return this.resolvedConfig()['footer1'] as unknown as string | undefined;
  }

  get footer2(): string | undefined {
    return this.resolvedConfig()['footer2'] as unknown as string | undefined;
  }

  get displayOrderReadyAt(): boolean {
    return this.resolvedConfig()['display_order_ready_at'] as unknown as boolean;
  }

  /** Default true — keeps legacy full-viewport kiosk confirmation layout. */
  get fillViewport(): boolean {
    return this.resolvedConfig()['fill_viewport'] as unknown as boolean;
  }

  order?: DoodOrderModel;
  orderReadyAt = '';
  receiptPrinted = false;

  constructor(
    @Inject(KIOSK_ORDER_STATUS_BLOCK_DATA_SOURCE)
    blockDataSource: KioskOrderStatusBlockDataSource,
    private readonly resetStateService: ResetStateService,
  ) {
    super();

    blockDataSource.vm$.pipe(takeUntil(this._destroyerRef)).subscribe(vm => {
      this.order = vm.order;
      this.orderReadyAt = vm.orderReadyAt;
      this.receiptPrinted = vm.receiptPrinted;
    });
  }

  async goToHome() {
    await this.resetStateService.refreshUIAndResetLang();
  }

  protected getBackgroundStyles() {
    return {
      'background-color': this.backgroundColor ? this.backgroundColor : null,
      'background-image': this.backgroundImage ? `url(${this.backgroundImage.url})` : null,
      'background-size': this.backgroundImage ? 'cover' : null,
    };
  }
}

COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskOrderStatusBlockComponent, {
  'keyAliases': {},
});
